/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/no-multi-comp */
import React from 'react';
import { Field } from 'react-final-form';
import { Grid, Header, Form } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { FieldArray } from 'react-final-form-arrays';
import { MdAdd } from 'react-icons/md';
import { v4 as uuidV4 } from 'uuid';

import { AppState } from 'services/types';
import ButtonLink from 'components/ButtonLink';
import ButtonIconWrapper from 'components/ButtonIconWrapper';
import {
    VirtualizedTableListColumn,
    VirtualizedTableProps
} from 'services/virtualizedTable/types';
import {
    makeSelectVirtualizedTableColumnFormHeader,
    makeSelectVirtualizedTableColumnFormColumnWidth,
    makeSelectVirtualizedTableColumnType,
    makeSelectVirtualizedTableColumnLabel,
    makeSelectVirtualizedTableColumnValue,
    makeSelectVirtualizedTableColumnPlaceholder,
    makeSelectVirtualizedTableColumnOptions,
    makeSelectVirtualizedTableColumnValidators,
    makeSelectVirtualizedTableColumnDisableUpdating,
    makeSelectVirtualizedTableColumnListColumns,
    makeSelectVirtualizedTableColumnEmptyValueToNull,
    makeSelectVirtualizedTableColumnFieldValue
} from 'services/virtualizedTable/selectors';
import getValidateFunction from 'forms/utils/get-validate-function';

import VirtualizedTableColumnListItemWrapper from '../VirtualizedTableColumnListItemWrapper';
import VirtualizedTableColumnListItem from '../VirtualizedTableColumnListItem';
import {
    FIELD_TYPE_DROPDOWN,
    FIELD_TYPE_DROPDOWN_MULTIPLE,
    FIELD_TYPE_STRING,
    FIELD_TYPE_NUMBER,
    FIELD_TYPE_PASSWORD,
    FIELD_TYPE_DATE,
    FIELD_TYPE_LIST,
    FIELD_TYPE_CONFIRMATION_CHECKBOX
} from '../../constants';
import getColumnTypeFieldProps from '../../utils/get-column-type-field-props';

import './styles.scss';

const subscription = {
    value: true,
    touched: true,
    error: true
};

interface Props extends VirtualizedTableProps {
    ignoreFormFormatting?: boolean;
    isEditForm?: boolean;
    hiddenFormFields?: string[];
    disabledFormFields?: string[];
}

export const VirtualizedTableItemValueFieldComponent = (
    props: Props
): React.ReactElement | null => {
    const formHeader = useSelector((state: AppState) =>
        makeSelectVirtualizedTableColumnFormHeader()(state, props)
    );
    const formColumnWidth = useSelector((state: AppState) =>
        makeSelectVirtualizedTableColumnFormColumnWidth()(state, props)
    );
    const columnType = useSelector((state: AppState) =>
        makeSelectVirtualizedTableColumnType()(state, props)
    );
    const columnLabel = useSelector((state: AppState) =>
        makeSelectVirtualizedTableColumnLabel()(state, props)
    );
    const columnValue = useSelector((state: AppState) =>
        makeSelectVirtualizedTableColumnValue()(state, props)
    );
    const columnPlaceholder = useSelector((state: AppState) =>
        makeSelectVirtualizedTableColumnPlaceholder()(state, props)
    );
    const columnOptions = useSelector((state: AppState) =>
        makeSelectVirtualizedTableColumnOptions()(state, props)
    );
    const columnValidators = useSelector((state: AppState) =>
        makeSelectVirtualizedTableColumnValidators()(state, props)
    );
    const columnDisableUpdating = useSelector((state: AppState) =>
        makeSelectVirtualizedTableColumnDisableUpdating()(state, props)
    );
    const columnListColumns = useSelector((state: AppState) =>
        makeSelectVirtualizedTableColumnListColumns()(state, props)
    );
    const columnEmptyValueToNull = useSelector((state: AppState) =>
        makeSelectVirtualizedTableColumnEmptyValueToNull()(state, props)
    );
    const formClassName = useSelector((state: AppState) =>
        makeSelectVirtualizedTableColumnFieldValue('formClassName')(
            state,
            props
        )
    );

    const fieldProps = useSelector((state: AppState) =>
        makeSelectVirtualizedTableColumnFieldValue('fieldProps')(state, props)
    );

    const isDropdown =
        columnType === FIELD_TYPE_DROPDOWN ||
        columnType === FIELD_TYPE_DROPDOWN_MULTIPLE;

    const validate = getValidateFunction(columnValidators);

    const validators = Array.isArray(columnValidators)
        ? columnValidators
        : [columnValidators];

    const isRequired = validators.length
        ? validators.some(
              (validator) => !!validator && validator.includes('isRequired')
          )
        : false;

    const { columnKey, ignoreFormFormatting, isEditForm, disabledFormFields } =
        props;

    if (!columnKey) {
        return null;
    }

    const parse = columnEmptyValueToNull
        ? (v: string): string | null => (v === '' ? null : v)
        : undefined;

    const isDisabled =
        (isEditForm && columnDisableUpdating) ||
        disabledFormFields?.includes(columnValue as string);

    const sharedProps = {
        name: columnKey,
        label: `${columnLabel}${isRequired ? ' (required)' : ''}`,
        placeholder:
            columnPlaceholder ||
            `${isDropdown ? 'Add' : 'Enter'} ${columnLabel}`,
        subscription,
        validate,
        parse,
        disabled: isDisabled,
        ...(isDropdown
            ? {
                  options: columnOptions
              }
            : {})
    };

    const renderField = (): React.ReactElement | null => {
        if (
            [
                FIELD_TYPE_STRING,
                FIELD_TYPE_NUMBER,
                FIELD_TYPE_PASSWORD
            ].includes(columnType!)
        ) {
            return (
                <Field
                    {...sharedProps}
                    {...getColumnTypeFieldProps(columnType)}
                    inputProps={fieldProps}
                />
            );
        }

        if (
            [FIELD_TYPE_CONFIRMATION_CHECKBOX, FIELD_TYPE_DATE].includes(
                columnType!
            )
        ) {
            return (
                <Field
                    {...sharedProps}
                    {...getColumnTypeFieldProps(columnType)}
                />
            );
        }

        if (
            [FIELD_TYPE_DROPDOWN, FIELD_TYPE_DROPDOWN_MULTIPLE].includes(
                columnType!
            )
        ) {
            return (
                <Field
                    {...sharedProps}
                    {...getColumnTypeFieldProps(columnType)}
                    // Fix to avoid dropdown not validating on submit
                    clearable={!validate}
                    {...sharedProps.name === 'harvestEvent.harvestSubmissionProfileId' ? { initialValue: null } : {}}
                />
            );
        }

        if (columnType === FIELD_TYPE_LIST) {
            const { hiddenFormFields } = props;
            const updatedListColumns =
                hiddenFormFields?.length && columnListColumns?.length
                    ? columnListColumns.filter(
                          (listColumn) =>
                              !hiddenFormFields.includes(
                                  `${columnValue}.${listColumn.value}`
                              )
                      )
                    : columnListColumns;

            return (
                <React.Fragment>
                    <Header as="h3">{columnLabel}</Header>
                    <FieldArray name={columnKey}>
                        {({ fields }): React.ReactElement => (
                            <React.Fragment>
                                {!fields.length ? null : (
                                    <div className={formClassName}>
                                        {fields.map((name, index) => (
                                            <React.Fragment key={name}>
                                                {!index && (
                                                    <VirtualizedTableColumnListItemWrapper
                                                        isHeader
                                                        left={
                                                            <Grid columns="equal">
                                                                <Grid.Row>
                                                                    {updatedListColumns &&
                                                                        updatedListColumns.map(
                                                                            (
                                                                                column: VirtualizedTableListColumn
                                                                            ) => {
                                                                                const {
                                                                                    value,
                                                                                    label
                                                                                } =
                                                                                    column;

                                                                                return (
                                                                                    <Grid.Column
                                                                                        key={
                                                                                            value
                                                                                        }
                                                                                    >
                                                                                        <Form.Field
                                                                                            style={{
                                                                                                marginBottom: 7
                                                                                            }}
                                                                                        >
                                                                                            <label
                                                                                                style={{
                                                                                                    whiteSpace:
                                                                                                        'nowrap',
                                                                                                    overflow:
                                                                                                        'hidden',
                                                                                                    textOverflow:
                                                                                                        'ellipsis'
                                                                                                }}
                                                                                                title={
                                                                                                    label
                                                                                                }
                                                                                            >
                                                                                                {
                                                                                                    label
                                                                                                }
                                                                                            </label>
                                                                                        </Form.Field>
                                                                                    </Grid.Column>
                                                                                );
                                                                            }
                                                                        )}
                                                                </Grid.Row>
                                                            </Grid>
                                                        }
                                                    />
                                                )}
                                                <VirtualizedTableColumnListItem
                                                    key={fields.value[
                                                        index
                                                    ].id.toString()}
                                                    name={name}
                                                    isEditForm={isEditForm}
                                                    columnListColumns={
                                                        updatedListColumns
                                                    }
                                                    onClick={(): void =>
                                                        fields.remove(index)
                                                    }
                                                    disabled={
                                                        sharedProps.disabled
                                                    }
                                                />
                                            </React.Fragment>
                                        ))}
                                    </div>
                                )}
                                <Grid padded>
                                    {fields.length === 0 && (
                                        <Grid.Row>
                                            <Grid.Column
                                                width={16}
                                                textAlign="center"
                                                className="VirtualizedTableItemValueFieldMessage"
                                            >
                                                No {columnLabel}
                                            </Grid.Column>
                                        </Grid.Row>
                                    )}
                                    <Grid.Column width={16} textAlign="center">
                                        <ButtonLink
                                            buttonProps={{
                                                primary: true,
                                                type: 'button',
                                                disabled: sharedProps.disabled
                                            }}
                                            // HACK: Set a uuid as the id for new rows for React keys
                                            onClick={(): void =>
                                                fields.push({ id: uuidV4() })
                                            }
                                        >
                                            <ButtonIconWrapper>
                                                <MdAdd
                                                    style={{
                                                        fontSize: 20
                                                    }}
                                                />
                                            </ButtonIconWrapper>
                                            Add New {columnLabel}
                                        </ButtonLink>
                                    </Grid.Column>
                                </Grid>
                            </React.Fragment>
                        )}
                    </FieldArray>
                </React.Fragment>
            );
        }

        return null;
    };

    if (ignoreFormFormatting) {
        return <Grid.Column width={16}>{renderField()}</Grid.Column>;
    }

    return (
        <React.Fragment>
            {formHeader && (
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Header as="h3" style={{ margin: 0 }}>
                            {formHeader}
                        </Header>
                    </Grid.Column>
                </Grid.Row>
            )}
            <Grid.Column
                mobile={16}
                tablet={formColumnWidth || 16}
                computer={formColumnWidth || 16}
            >
                {renderField()}
            </Grid.Column>
        </React.Fragment>
    );
};

export default VirtualizedTableItemValueFieldComponent;
